import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  PenLine,
  DollarSign,
  Type,
  CheckSquare,
  ListOrdered,
  CircleDot,
} from 'lucide-react'
import { useProjectStore } from '../store/projectStore.js'

const KIND_ICON = {
  name: User,
  email: Mail,
  phone: Phone,
  date: Calendar,
  address: MapPin,
  signature: PenLine,
  amount: DollarSign,
  text: Type,
}

const TYPE_ICON = {
  checkbox: CheckSquare,
  dropdown: ListOrdered,
  radio: CircleDot,
}

export default function FormEditor({ doc }) {
  const updateFormFieldValue = useProjectStore((s) => s.updateFormFieldValue)
  const fields = doc.formFields || []

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-slate-500">
        Detected {fields.length} field{fields.length === 1 ? '' : 's'}. Values are written into the PDF on export.
      </p>
      <div className="space-y-2.5">
        {fields.map((field) => {
          const IconComp =
            TYPE_ICON[field.type] || KIND_ICON[field.kind] || Type
          return (
            <label key={field.name} className="block">
              <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-600">
                <IconComp className="h-3 w-3 text-slate-400" />
                <span className="truncate" title={field.name}>
                  {field.name}
                </span>
                <span className="ml-auto rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-normal uppercase tracking-wider text-slate-500">
                  {field.type}
                </span>
              </span>

              {field.type === 'text' && (
                <input
                  type={
                    field.kind === 'email'
                      ? 'email'
                      : field.kind === 'date'
                      ? 'date'
                      : field.kind === 'amount'
                      ? 'number'
                      : 'text'
                  }
                  value={field.value || ''}
                  placeholder={`Enter ${field.kind}`}
                  onChange={(e) =>
                    updateFormFieldValue(doc.id, field.name, e.target.value)
                  }
                  className="input h-9 text-xs"
                />
              )}

              {field.type === 'checkbox' && (
                <button
                  type="button"
                  onClick={() =>
                    updateFormFieldValue(doc.id, field.name, !field.value)
                  }
                  className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition ${
                    field.value
                      ? 'border-brand-300 bg-brand-50 text-brand-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded border ${
                      field.value
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {field.value && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  {field.value ? 'Checked' : 'Unchecked'}
                </button>
              )}

              {(field.type === 'dropdown' || field.type === 'radio') && (
                <select
                  value={field.value || ''}
                  onChange={(e) =>
                    updateFormFieldValue(doc.id, field.name, e.target.value)
                  }
                  className="input h-9 text-xs"
                >
                  <option value="">— Select —</option>
                  {(field.options || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}

              {field.type === 'unknown' && (
                <p className="text-[11px] text-slate-400">
                  Unsupported field type — will be preserved as-is.
                </p>
              )}
            </label>
          )
        })}
      </div>
    </div>
  )
}
